import React, { useState, useEffect, useRef } from "react";
import { uploadCoverImage } from "../api/blogapi";
import {
  FiImage,
  FiVideo,
  FiEye,
  FiEdit3,
  FiUploadCloud,
  FiBold,
  FiItalic,
  FiList,
  FiLink,
  FiCheck,
} from "react-icons/fi";
import { FaMagic } from "react-icons/fa";

export default function BlogForm({ blogToEdit, onSubmit, onCancel }) {
  const [title, setTitle] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [mediaType, setMediaType] = useState("image"); // 'image' | 'video'
  const [content, setContent] = useState("");
  const [summary, setSummary] = useState("");
  const [author, setAuthor] = useState("JAPL Team");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("edit"); // 'edit' | 'preview'
  const textareaRef = useRef(null);

  // Pre-fill form when editing
  useEffect(() => {
    if (blogToEdit) {
      setTitle(blogToEdit.title || "");
      setCoverImage(blogToEdit.coverImage || "");
      setMediaType(blogToEdit.mediaType || "image");
      setContent(blogToEdit.content || "");
      setSummary(blogToEdit.summary || "");
      setAuthor(blogToEdit.author || "JAPL Team");
      setError(null);
    } else {
      setTitle("");
      setCoverImage("");
      setMediaType("image");
      setContent("");
      setSummary("");
      setAuthor("JAPL Team");
      setError(null);
    }
  }, [blogToEdit]);

  // Handle Cover File Upload (Image or Video)
  const handleCoverChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setUploading(true);

    try {
      const res = await uploadCoverImage(file);
      const url = typeof res === "string" ? res : res.url;
      const detectedType =
        (typeof res === "object" && res.mediaType) ||
        (file.type.startsWith("video/") ? "video" : "image");

      const fullUrl = url.startsWith("http")
        ? url
        : `http://localhost:5000${url}`;

      setCoverImage(fullUrl);
      setMediaType(detectedType);
    } catch (err) {
      console.error(err);
      const serverMsg = err.response?.data?.message || err.message;
      setError(serverMsg || "Failed to upload cover file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Helper function to insert HTML tags at cursor position in text area
  const insertFormatting = (startTag, endTag = "", defaultText = "") => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setContent((prev) => prev + `${startTag}${defaultText}${endTag}`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end) || defaultText;
    const replacement = `${startTag}${selectedText}${endTag}`;

    const newContent =
      content.substring(0, start) + replacement + content.substring(end);
    setContent(newContent);

    // Set cursor position after edit
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + startTag.length,
        start + startTag.length + selectedText.length
      );
    }, 50);
  };

  // Helper tool to insert an inline image URL
  const insertImageModal = () => {
    const url = prompt("Enter Image URL to embed inside the blog:");
    if (!url) return;
    const caption = prompt("Enter Image Caption (optional):") || "";
    const html = `\n<figure className="my-6 text-center">\n  <img src="${url}" alt="${caption}" class="rounded-xl shadow-md max-h-96 w-full object-cover mx-auto" />\n  ${
      caption ? `<figcaption class="text-xs text-gray-500 mt-2">${caption}</figcaption>` : ""
    }\n</figure>\n`;
    insertFormatting(html);
  };

  // Helper tool to insert a video / YouTube embed
  const insertVideoModal = () => {
    const url = prompt("Enter Direct MP4 Video URL or YouTube Link:");
    if (!url) return;

    let html = "";
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      let videoId = "";
      if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1]?.split("?")[0];
      } else {
        videoId = new URLSearchParams(new URL(url).search).get("v");
      }
      html = `\n<div class="my-6 aspect-video rounded-xl overflow-hidden shadow-lg">\n  <iframe src="https://www.youtube.com/embed/${videoId}" title="Blog Video" class="w-full h-full border-0" allowfullscreen></iframe>\n</div>\n`;
    } else {
      html = `\n<div class="my-6 rounded-xl overflow-hidden shadow-lg bg-black">\n  <video src="${url}" controls class="w-full max-h-96 object-contain mx-auto"></video>\n</div>\n`;
    }
    insertFormatting(html);
  };

  // Smart Auto-Format plain text into clean HTML structure
  const handleSmartAutoFormat = () => {
    if (!content.trim()) {
      alert("Please write or paste some text first into the content area!");
      return;
    }

    // Check if content already contains HTML tags
    if (/<[a-z][\s\S]*>/i.test(content)) {
      if (
        !window.confirm(
          "Your text already has HTML tags. Auto-formatting will clean and re-structure it. Continue?"
        )
      ) {
        return;
      }
    }

    // Process raw text line by line
    const lines = content.split("\n");
    let formattedHtml = [];
    let inList = false;

    lines.forEach((line) => {
      const trimmed = line.trim();

      if (!trimmed) {
        if (inList) {
          formattedHtml.push("</ul>");
          inList = false;
        }
        return;
      }

      // Check for bullet list item
      if (trimmed.startsWith("- ") || trimmed.startsWith("* ") || /^\d+\.\s/.test(trimmed)) {
        const itemText = trimmed.replace(/^([-*]|\d+\.)\s*/, "");
        if (!inList) {
          formattedHtml.push('<ul class="list-disc list-inside space-y-2 my-4 text-gray-700 pl-2">');
          inList = true;
        }
        formattedHtml.push(`  <li>${itemText}</li>`);
        return;
      }

      if (inList) {
        formattedHtml.push("</ul>");
        inList = false;
      }

      // Heading 2 (e.g. starts with # or ends with :)
      if (trimmed.startsWith("# ")) {
        formattedHtml.push(
          `<h2 class="text-2xl font-bold text-gray-900 mt-8 mb-3 border-b pb-2">${trimmed.replace(/^#\s*/, "")}</h2>`
        );
      } else if (trimmed.startsWith("## ")) {
        formattedHtml.push(
          `<h3 class="text-xl font-semibold text-gray-800 mt-6 mb-2">${trimmed.replace(/^##\s*/, "")}</h3>`
        );
      } else if (trimmed.startsWith("> ")) {
        formattedHtml.push(
          `<blockquote class="border-l-4 border-blue-600 bg-blue-50/60 pl-4 py-3 my-4 rounded-r text-gray-700 italic font-medium">${trimmed.replace(/^>\s*/, "")}</blockquote>`
        );
      } else if (trimmed.length < 60 && !trimmed.endsWith(".") && !trimmed.endsWith(",")) {
        // Short lines treated as section headings automatically
        formattedHtml.push(
          `<h2 class="text-xl font-bold text-gray-900 mt-6 mb-3">${trimmed}</h2>`
        );
      } else {
        formattedHtml.push(
          `<p class="mb-4 text-gray-700 leading-relaxed">${trimmed}</p>`
        );
      }
    });

    if (inList) {
      formattedHtml.push("</ul>");
    }

    setContent(formattedHtml.join("\n"));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title || !coverImage || !content) {
      setError("Please fill in the title, cover media (image/video), and blog content.");
      return;
    }

    onSubmit({
      title,
      coverImage,
      mediaType,
      content,
      summary,
      author,
    });
  };

  // Helper to render video player or iframe for YouTube
  const renderVideoPlayer = (url) => {
    if (!url) return null;
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      let videoId = "";
      if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1]?.split("?")[0];
      } else {
        try {
          videoId = new URLSearchParams(new URL(url).search).get("v");
        } catch {
          videoId = "";
        }
      }
      return (
        <div className="aspect-video w-full rounded-xl overflow-hidden shadow">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="Video Banner"
            className="w-full h-full border-0"
            allowFullScreen
          />
        </div>
      );
    }

    return (
      <video
        src={url}
        controls
        className="w-full h-56 object-cover rounded-xl border shadow"
      />
    );
  };

  return (
    <div className="bg-white shadow-2xl rounded-3xl p-6 md:p-8 mb-10 border border-gray-100 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b pb-4 mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">
            {blogToEdit ? "✏️ Edit Blog Post" : "✨ Create New Blog Post"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Easily upload blogs with Image/Video banners and smart formatting options.
          </p>
        </div>

        {/* Edit / Preview Tabs */}
        <div className="flex items-center bg-gray-100 p-1 rounded-xl">
          <button
            type="button"
            onClick={() => setActiveTab("edit")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === "edit"
                ? "bg-white text-blue-700 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <FiEdit3 /> Editor Mode
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("preview")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === "preview"
                ? "bg-white text-blue-700 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <FiEye /> Live Preview
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium">
          {error}
        </div>
      )}

      {activeTab === "preview" ? (
        /* LIVE PREVIEW MODE */
        <div className="bg-gray-50 rounded-2xl p-6 border">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-6 md:p-8">
            <div className="mb-4 text-xs font-semibold uppercase tracking-wider text-blue-600">
              {author || "JAPL Team"} • Live Preview
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {title || "Untitled Blog Post"}
            </h1>

            {/* Banner Preview */}
            {coverImage ? (
              <div className="mb-6">
                {mediaType === "video" ? (
                  renderVideoPlayer(coverImage)
                ) : (
                  <img
                    src={coverImage}
                    alt={title}
                    className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-md"
                  />
                )}
              </div>
            ) : (
              <div className="w-full h-48 bg-gray-100 rounded-2xl border flex items-center justify-center text-gray-400 text-sm mb-6">
                No Banner Selected
              </div>
            )}

            {summary && (
              <p className="text-lg text-gray-600 italic mb-6 border-l-4 border-blue-500 pl-4 py-1">
                {summary}
              </p>
            )}

            <div
              className="prose max-w-none text-gray-800 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: content || "<p class='text-gray-400 italic'>Blog content will appear here...</p>",
              }}
            />
          </div>
        </div>
      ) : (
        /* EDITOR MODE */
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-gray-800 font-bold mb-2 text-sm">
              Blog Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              placeholder="e.g. Understanding Modern Vehicle Maintenance Tips"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Author & Short Excerpt */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-800 font-bold mb-2 text-sm">
                Author Name
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Author name (default: JAPL Team)"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-gray-800 font-bold mb-2 text-sm">
                Short Excerpt / Summary
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief 1-2 sentence description of blog"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
              />
            </div>
          </div>

          {/* Banner Media Choice (Image or Video) */}
          <div className="bg-gray-50/80 p-5 rounded-2xl border border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <label className="block text-gray-800 font-bold text-sm">
                Blog Banner Media (Image or Video) <span className="text-red-500">*</span>
              </label>

              {/* Media Type Selector */}
              <div className="flex items-center gap-2 bg-white border p-1 rounded-xl shadow-sm">
                <button
                  type="button"
                  onClick={() => setMediaType("image")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    mediaType === "image"
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <FiImage /> Image Banner
                </button>
                <button
                  type="button"
                  onClick={() => setMediaType("video")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    mediaType === "video"
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <FiVideo /> Video Banner
                </button>
              </div>
            </div>

            {/* File Upload or Media URL input */}
            <div className="grid md:grid-cols-2 gap-4 items-center">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Upload {mediaType === "video" ? "Video File" : "Image File"}:
                </label>
                <input
                  type="file"
                  accept={mediaType === "video" ? "video/*" : "image/*"}
                  onChange={handleCoverChange}
                  className="block w-full text-xs text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 cursor-pointer"
                />
                {uploading && (
                  <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                    <FiUploadCloud className="animate-bounce" /> Uploading banner file...
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Or Paste Direct {mediaType === "video" ? "Video / YouTube URL" : "Image URL"}:
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    mediaType === "video"
                      ? "https://...mp4 or YouTube link"
                      : "https://...jpg or png URL"
                  }
                  value={coverImage}
                  onChange={(e) => setCoverImage(e.target.value)}
                />
              </div>
            </div>

            {/* Media Live Preview */}
            {coverImage && (
              <div className="mt-4 pt-3 border-t">
                <p className="text-xs font-semibold text-gray-500 mb-2">
                  Banner Preview ({mediaType.toUpperCase()}):
                </p>
                {mediaType === "video" ? (
                  renderVideoPlayer(coverImage)
                ) : (
                  <img
                    src={coverImage}
                    alt="Cover preview"
                    className="h-44 w-full object-cover rounded-xl border shadow-sm"
                  />
                )}
              </div>
            )}
          </div>

          {/* Content Editor with Formatting Bar */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
              <label className="block text-gray-800 font-bold text-sm">
                Blog Content <span className="text-red-500">*</span>
              </label>

              {/* Smart Auto-Format Button */}
              <button
                type="button"
                onClick={handleSmartAutoFormat}
                className="flex items-center gap-1.5 text-xs bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-3 py-1.5 rounded-xl font-medium shadow-sm transition transform active:scale-95"
                title="Automatically format plain text into clean styled headings, paragraphs & lists"
              >
                <FaMagic className="text-yellow-300" /> ✨ Smart Auto-Format Content
              </button>
            </div>

            {/* Formatting Toolbar */}
            <div className="flex flex-wrap items-center gap-1.5 bg-gray-100 p-2 rounded-t-xl border border-b-0">
              <button
                type="button"
                onClick={() => insertFormatting('<h2 class="text-2xl font-bold text-gray-900 mt-6 mb-3">', '</h2>', 'Section Heading')}
                className="px-2.5 py-1 text-xs font-bold bg-white rounded border hover:bg-gray-50 text-gray-800"
                title="Heading 2"
              >
                H2
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('<h3 class="text-xl font-semibold text-gray-800 mt-4 mb-2">', '</h3>', 'Subheading')}
                className="px-2.5 py-1 text-xs font-semibold bg-white rounded border hover:bg-gray-50 text-gray-800"
                title="Heading 3"
              >
                H3
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('<p class="mb-4 text-gray-700 leading-relaxed">', '</p>', 'Paragraph text...')}
                className="px-2.5 py-1 text-xs bg-white rounded border hover:bg-gray-50 text-gray-800"
                title="Paragraph"
              >
                Paragraph
              </button>
              <div className="h-4 w-px bg-gray-300 mx-1" />
              <button
                type="button"
                onClick={() => insertFormatting("<strong>", "</strong>", "bold text")}
                className="p-1.5 bg-white rounded border hover:bg-gray-50 text-gray-800"
                title="Bold"
              >
                <FiBold />
              </button>
              <button
                type="button"
                onClick={() => insertFormatting("<em>", "</em>", "italic text")}
                className="p-1.5 bg-white rounded border hover:bg-gray-50 text-gray-800"
                title="Italic"
              >
                <FiItalic />
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('<ul class="list-disc list-inside space-y-2 my-4 text-gray-700">\n  <li>', '</li>\n  <li>Item 2</li>\n</ul>', 'List Item 1')}
                className="p-1.5 bg-white rounded border hover:bg-gray-50 text-gray-800"
                title="Bullet List"
              >
                <FiList />
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('<blockquote class="border-l-4 border-blue-600 bg-blue-50/60 pl-4 py-3 my-4 rounded-r text-gray-700 italic font-medium">', '</blockquote>', 'Important quote or note...')}
                className="px-2 py-1 text-xs font-serif italic bg-white rounded border hover:bg-gray-50 text-gray-800"
                title="Quote Callout"
              >
                Quote
              </button>
              <div className="h-4 w-px bg-gray-300 mx-1" />
              <button
                type="button"
                onClick={insertImageModal}
                className="flex items-center gap-1 px-2.5 py-1 text-xs bg-white rounded border hover:bg-gray-50 text-gray-800"
                title="Insert Image"
              >
                <FiImage /> Image
              </button>
              <button
                type="button"
                onClick={insertVideoModal}
                className="flex items-center gap-1 px-2.5 py-1 text-xs bg-white rounded border hover:bg-gray-50 text-gray-800"
                title="Insert Video"
              >
                <FiVideo /> Video
              </button>
            </div>

            <textarea
              ref={textareaRef}
              className="w-full border border-gray-300 rounded-b-xl px-4 py-3 h-72 resize-y text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-inner"
              placeholder="Write or paste your blog text here... 

Tip: You can paste normal text and click '✨ Smart Auto-Format Content' above to format it into beautiful headings, paragraphs, and lists instantly!"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>

          {/* Form Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={uploading}
              className="flex items-center gap-2 px-7 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition disabled:opacity-60"
            >
              <FiCheck className="text-lg" />
              {blogToEdit ? "Update Blog Post" : "Publish Blog Post"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
